import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PriceLabel from '../PriceLabel';
import LegalText from '../LegalText';
import {
  ModelContainer,
  ModelPriceLabel,
  ModelPrice,
  LegalTextContainer,
} from './DealerPriceInfo.style';
import type { CarPricesType } from '../../types/oneGraphTypes';
import { formatPrice } from '../../utils/currencyTools';

interface PriceInfoTypes {
  carType: string;
  market: string;
  modelYear: number;
  carPrices: CarPricesType[];
  isAvailableNow: boolean;
}

const PriceInfoT1: React.FC<PriceInfoTypes> = ({
  carType,
  market,
  modelYear,
  carPrices,
  isAvailableNow,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const isFrench = language === 'fr';
  const vehicleType = carType === 'N' ? 'new' : 'used';
  const modelPrice = carPrices.find((price) => price.type === 'list')?.price?.value; // regular price
  const modelPriceRetail = carPrices.find((price) => price.type === 'sale')?.price.value; // retail price
  const isMarketUSA = market.includes('us');
  const availableSoonCopy = t('vehiclePricingBar.dealerListPrice.availableSoon.label');

  const correctPrice = useMemo(() => {
    // USA Logic
    if (isMarketUSA) {
      if (!isAvailableNow && vehicleType === 'new') return availableSoonCopy;
      if (!modelPrice || modelPrice === 0) return availableSoonCopy;
      return formatPrice(modelPrice, isFrench);
    }
    // CA logic
    if (!modelPriceRetail || modelPriceRetail === 0) return availableSoonCopy;
    return formatPrice(modelPriceRetail, isFrench);
  }, [
    availableSoonCopy,
    isAvailableNow,
    isFrench,
    isMarketUSA,
    modelPrice,
    modelPriceRetail,
    vehicleType,
  ]);

  return (
    <ModelContainer>
      {/* CANADA => RETAIL (SALE) */}
      {/* USA => REGULAR (LIST) */}
      <ModelPriceLabel data-testid="price-label">
        <PriceLabel market={market} isAvailableNow={isAvailableNow} />
        <ModelPrice data-vehicle-price={modelPrice} data-testid="model-price">
          {correctPrice}*
        </ModelPrice>
      </ModelPriceLabel>
      <LegalTextContainer data-testid="price-legal-text">
        * <LegalText market={market} modelYear={modelYear} />
      </LegalTextContainer>
    </ModelContainer>
  );
};

export default PriceInfoT1;
